import { injectable } from 'inversify';
import { plainToClass } from 'class-transformer';

import { HttpService } from '@vk-hr-tek/core/http';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { FiltersResponse, Filter, FilterService } from '@vk-hr-tek/core/filter';

import {
  BatchEventAcceptResponse,
  BatchEventCancelResponse,
  BatchEventOperationResponse,
  CancelReasonListResponse as CancelReasonsResponse,
  EventListResponse,
  GetEventFileHashesResponse,
} from '@app/gen/events';

import {
  CancelBatchEventsDto,
  GetReviewDto,
  GetReviewFiltersDto,
  SendBatchEventsDto,
  GetHashesDto,
  UkepBatchSignDto,
} from '../dto';
import { FilterType } from '../types';

@injectable()
export class EventsListService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private filterService: FilterService,
  ) {}

  async get(getEventsDto: GetReviewDto, availableFilters: Filter[]) {
    const eventsDto = plainToClass(
      this.filterService.buildDto(GetReviewDto, availableFilters),
      getEventsDto,
    );

    if (!eventsDto.filters?.preset_id) {
      await this.validator.validateOrReject(eventsDto);
    }

    const filters: FilterType = {};

    for (const filter in eventsDto.filters) {
      const filterValue = eventsDto.filters[filter];
      this.filterService.sanitizeDates(filterValue);
      filters[filter] = filterValue;
    }

    if (eventsDto.tags) filters.tags = eventsDto.tags;
    if (eventsDto.companyId) {
      if (filters.company) {
        const filteredCompany = (filters.company as string[]).filter(
          (id) => id !== eventsDto.companyId,
        );
        filters.company = [...filteredCompany, eventsDto.companyId];
      } else {
        filters.company = [eventsDto.companyId];
      }
    }

    const {
      limit = 50,
      offset = 0,
      sortBy = 'created_at',
      sortOrder = 'desc',
      sortByIds,
    } = eventsDto;

    const checkForFilterByEventIds = !!sortByIds?.length
      ? { event_ids: sortByIds }
      : {};

    const result = await this.unmarshaller.unmarshall(
      await this.http.post(
        '/event/list',
        {
          pagination: {
            limit,
            offset,
            sort_by: sortBy,
            sort_order: sortOrder,
          },
          filters: filters
            ? { ...filters, event_ids: sortByIds }
            : checkForFilterByEventIds,
        },
        { withSide: true, reloadIfOldVersion: true },
      ),
      EventListResponse,
    );

    return {
      data: result.events,
      total: result.total_count,
      totalMy: result.total_count_my,
      totalMyTeam: result.total_count_my_group,
      finished: result.finished_count || 0,
    };
  }

  async getFilters(getFiltersDto?: GetReviewFiltersDto) {
    if (getFiltersDto) {
      await this.validator.validateOrReject(getFiltersDto, GetReviewFiltersDto);
    }

    const result = await this.unmarshaller.unmarshall(
      await this.http.get(
        '/event/available_filters',
        {
          ...(!!getFiltersDto?.page && { page: getFiltersDto.page }),
        },
        { withSide: true, reloadIfOldVersion: true },
      ),
      FiltersResponse,
    );

    return result.filters;
  }

  async sendBatchAccept(sendBatchAcceptDto: SendBatchEventsDto) {
    await this.validator.validateOrReject(
      sendBatchAcceptDto,
      SendBatchEventsDto,
    );

    const result = await this.unmarshaller.unmarshall(
      this.http.post(
        '/event/batch/accept',
        {
          events: sendBatchAcceptDto.events,
        },
        {
          withSide: true,
          reloadIfOldVersion: true,
        },
      ),
      BatchEventAcceptResponse,
    );

    return {
      successes: result.successes || [],
      errors: result.errors || [],
    };
  }

  async sendBatchCancel(sendBatchCancelDto: CancelBatchEventsDto) {
    await this.validator.validateOrReject(
      sendBatchCancelDto,
      CancelBatchEventsDto,
    );

    const { events, reasonId, comment } = sendBatchCancelDto;

    const result = await this.unmarshaller.unmarshall(
      this.http.post(
        '/event/batch/cancel',
        {
          events,
          reason_id: reasonId,
          comment,
        },
        {
          withSide: true,
          reloadIfOldVersion: true,
        },
      ),
      BatchEventCancelResponse,
    );

    return {
      successes: result.successes || [],
      errors: result.errors || [],
    };
  }

  async getCancelReasons({ isCandidate }: { isCandidate?: boolean }) {
    const result = await this.unmarshaller.unmarshall(
      await this.http.get(
        '/event/cancel_reasons',
        isCandidate ? { is_candidate: isCandidate } : {},
        { withSide: true },
      ),
      CancelReasonsResponse,
    );

    return result.reasons;
  }

  async getHashes(getHashesDto: GetHashesDto) {
    const result = await this.unmarshaller.unmarshall(
      await this.http.post(`/event/batch/document/hash`, getHashesDto, {
        withSide: true,
      }),
      GetEventFileHashesResponse,
    );
    return result as GetEventFileHashesResponse;
  }

  async ukepBatchSign(ukepBatchSignDto: UkepBatchSignDto) {
    const result = await this.unmarshaller.unmarshall(
      await this.http.post('/event/batch/ukep_sign', ukepBatchSignDto, {
        withSide: true,
      }),
      BatchEventOperationResponse,
    );
    return result;
  }
}
